import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, EyeIcon } from '@heroicons/react/24/outline';
import Header from '../Header';
import Sidebar from '../Sidebar';
import watermarkImage from '../assets/coverzone-watermark.png';
import axiosInstance from '../api/axiosInstance';
import BrokerDetailsModal from './BrokerDetailsModal';
import { useRequireAuth } from '../hooks/useRequireAuth';
import { tokenService } from '../services/tokenService';

interface Broker {
  id: number;
  name: string;
  surname: string;
  email: string;
  phone: string;
  company_name: string;
  broker_number: string;
  status: string;
  created_at: string;
  inception_date?: string;
}

const MasterBrokerPage = () => {
  useRequireAuth();

  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [brokers, setBrokers] = useState<Broker[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [user, setUser] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedBrokerId, setSelectedBrokerId] = useState<number | null>(null);
  const [isReadOnly, setIsReadOnly] = useState(false);

  const toggleSidebar = () => {
    setIsSidebarVisible(!isSidebarVisible);
  };

  useEffect(() => {
    if (!tokenService.getToken()) return;
    const fetchUser = async () => {
      try {
        const response = await axiosInstance.get('/auth/me');
        setUser(response.data);
      } catch (error) {
        console.error('Error fetching user:', error);
      }
    };
    fetchUser();
  }, []);

  const fetchBrokers = async () => {
    try {
      const response = await axiosInstance.get('/api/brokers');
      setBrokers(response.data);
    } catch (error: any) {
      console.error('Error fetching brokers:', error);
      setError(error.response?.data?.message || 'Failed to load brokers');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!tokenService.getToken()) return;
    fetchBrokers();
  }, []);

  const handleAddBroker = () => {
    setSelectedBrokerId(null);
    setIsReadOnly(false);
    setIsModalOpen(true);
  };

  const handleEditBroker = (id: number) => {
    setSelectedBrokerId(id);
    setIsReadOnly(false);
    setIsModalOpen(true);
  };

  const handleViewBroker = (id: number) => {
    setSelectedBrokerId(id);
    setIsReadOnly(true);
    setIsModalOpen(true);
  };

  const handleDeleteBroker = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this broker?')) {
      try {
        await axiosInstance.delete(`/api/brokers/${id}`);
        setBrokers(brokers.filter(broker => broker.id !== id));
      } catch (error: any) {
        console.error('Error deleting broker:', error);
        setError(error.response?.data?.message || 'Failed to delete broker');
      }
    }
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedBrokerId(null);
    setIsReadOnly(false);
    fetchBrokers();
  };

  const formatDate = (dateString?: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const activeBrokers = brokers.filter(b =>
    b.status?.toLowerCase() === 'active' || !b.status
  );
  const pausedBrokers = brokers.filter(b =>
    b.status?.toLowerCase() === 'paused' || b.status?.toLowerCase() === 'inactive'
  );
  const cancelledBrokers = brokers.filter(b =>
    b.status?.toLowerCase() === 'cancelled' || b.status?.toLowerCase() === 'canceled'
  );

  const BrokerTable: React.FC<{
    title: string;
    brokers: Broker[];
    headerColor: string;
    headerBg: string;
  }> = ({ title, brokers, headerColor, headerBg }) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden mb-6">
      <div className={`px-6 py-4 ${headerBg} border-b border-gray-200`}>
        <h3 className={`text-lg font-semibold ${headerColor}`}>
          {title} ({brokers.length})
        </h3>
      </div>
      {brokers.length === 0 ? (
        <div className="p-8 text-center text-gray-500 italic">
          No brokers in this section
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Brokerage Name
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Inception Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {brokers.map((broker) => (
                <tr key={broker.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 text-sm text-gray-900">
                    {broker.company_name || `${broker.name} ${broker.surname}` || '-'}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-600">
                    {formatDate(broker.inception_date || broker.created_at)}
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleViewBroker(broker.id)}
                        className="text-blue-600 hover:text-blue-800"
                        title="View"
                      >
                        <EyeIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleEditBroker(broker.id)}
                        className="text-green-600 hover:text-green-800"
                        title="Edit"
                      >
                        <PencilIcon className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteBroker(broker.id)}
                        className="text-red-600 hover:text-red-800"
                        title="Delete"
                      >
                        <TrashIcon className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-100 flex relative">
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-0">
        <img
          src={watermarkImage}
          alt="Watermark"
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-100 h-100 opacity-5 z-0 select-none"
        />
      </div>

      <Sidebar isVisible={isSidebarVisible} toggleSidebar={toggleSidebar} />

      <div className={`flex-1 flex flex-col transition-all duration-300 ${isSidebarVisible ? 'ml-64' : 'ml-0'}`}>
        <Header
          title="Master Brokers"
          user={user}
          toggleSidebar={toggleSidebar}
          isSidebarVisible={isSidebarVisible}
        />

        <div className="flex-grow p-6">
          <div className="max-w-7xl mx-auto">
            <div className="flex justify-between items-center mb-6">
              <div>
                <h1 className="text-2xl font-semibold text-gray-900">Master Brokers</h1>
                <p className="text-sm text-gray-500">Manage all brokers in the system</p>
              </div>
              <button
                onClick={handleAddBroker}
                className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors"
              >
                <PlusIcon className="w-5 h-5 mr-2" />
                Add New Broker
              </button>
            </div>

            {error && (
              <div className="mb-4 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
                {error}
                <button onClick={() => setError('')} className="float-right text-red-700 hover:text-red-900">×</button>
              </div>
            )}

            {isLoading ? (
              <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
                <p className="text-gray-500">Loading brokers...</p>
              </div>
            ) : (
              <>
                <BrokerTable
                  title="Active Brokers"
                  brokers={activeBrokers}
                  headerColor="text-green-800"
                  headerBg="bg-green-50"
                />
                <BrokerTable
                  title="Paused Brokers"
                  brokers={pausedBrokers}
                  headerColor="text-yellow-800"
                  headerBg="bg-yellow-50"
                />
                <BrokerTable
                  title="Cancelled Brokers"
                  brokers={cancelledBrokers}
                  headerColor="text-red-800"
                  headerBg="bg-red-50"
                />
              </>
            )}
          </div>
        </div>
      </div>

      <BrokerDetailsModal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        onSuccess={handleModalClose}
        brokerId={selectedBrokerId}
        readOnly={isReadOnly}
      />
    </div>
  );
};

export default MasterBrokerPage;